import { Assets, Container, Graphics, RenderTexture, Sprite, TilingSprite } from 'pixi.js';
import type { Application, Texture } from 'pixi.js';
import { pointAtProgress, v, type PreparedTrack, type PropPlacement, type Vec2 } from '@nanocars/shared';
import { PROP_PATHS, SURFACE_PATHS, type PropKey } from '../assets/manifest';

// Roughly how large one repeat of the wood/tablecloth texture should read as
// on the table, in metres. Purely a visual tuning constant.
const WOOD_TILE_SIZE_M = 3;
const TABLECLOTH_TILE_SIZE_M = 2.2;

const FLOOR_COLOR = 0x1c1108; // darker than the table itself
const FLOOR_MARGIN_M = 40; // how far past the table the dark floor extends

// The soft shadow just outside the table edge, built as a stack of
// concentric "picture frame" bands (four rects each) with decreasing alpha,
// rather than a true gradient (pixi Graphics fills are flat colours; this
// reads as a soft falloff at the sizes involved without needing a shader).
const EDGE_BAND_COUNT = 6;
const EDGE_BAND_WIDTH_M = 0.5;
const EDGE_BAND_MAX_ALPHA = 0.45;
const EDGE_COLOR = 0x000000;

// Resolution of the skid-mark render texture, in texture pixels per world
// metre. Independent of the screen's px-per-metre - this only needs to look
// crisp at typical play zoom, not match it exactly.
const SKID_PX_PER_M = 24;
const SKID_MARK_LENGTH_PX = 5;
const SKID_MARK_WIDTH_PX = 2;
const SKID_COLOR = 0x181008;

// Track markings: dashed cream lines tracing both edges of the band (the
// centreline offset by half the width, with inner-corner loops pruned) and a
// checkered flag strip across the start/finish line at progress 0. There is
// deliberately no fill between the edges: Martin wants the bare wood to show
// with only the borders marking the course.

const EDGE_LINE_COLOR = 0xf6e7c8;
const EDGE_LINE_ALPHA = 0.85;
const EDGE_LINE_WIDTH_M = 0.12;
const EDGE_DASH_M = 0.8;
const EDGE_GAP_M = 0.5;
const EDGE_STEP_M = 0.15; // sampling step for tracing the dashed edge curve

const START_STRIP_DEPTH_M = 1.2;
const START_STRIP_SQUARE_M = 0.6;
const START_STRIP_CREAM = 0xf6e7c8;
const START_STRIP_DARK = 0x2a1d12;

// Prop sprite size, in world metres, from PropPlacement.scale: scale is
// "metres per 100 px of the sprite at native resolution" (see
// tracks/kitchen-table.ts's header comment) - a 768px sprite at scale 0.5 is
// 3.84m wide, i.e. metres = nativePx * scale / 100.
function propMetres(nativePx: number, scale: number): number {
  return (nativePx * scale) / 100;
}

function bboxOf(poly: Vec2[]): { min: Vec2; max: Vec2 } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of poly) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
}

// Everything track-shaped: the wood table, surface regions (milk spill,
// crumbs, tablecloth), the table edge/falloff floor, props (both layers),
// and the skid-mark render texture. All children are positioned in world
// metres - the caller's world container is expected to carry the
// px-per-metre scale, not this class.
export class TrackRenderer {
  // Everything under the cars: floor, wood, surface regions, table edge,
  // props (layer 'under'), and the skid-mark texture, in that draw order.
  readonly below = new Container();
  // Props with layer 'over', drawn above cars.
  readonly above = new Container();

  private skidTexture: RenderTexture | null = null;
  private readonly skidBrush = new Graphics();
  private readonly skidOriginX: number;
  private readonly skidOriginY: number;

  constructor(
    private readonly app: Application,
    private readonly track: PreparedTrack,
  ) {
    const { min } = track.bounds;
    this.skidOriginX = min.x;
    this.skidOriginY = min.y;
  }

  async build(): Promise<void> {
    const { min, max } = this.track.bounds;
    const w = max.x - min.x;
    const h = max.y - min.y;

    this.below.addChild(this.buildFloor(min, max));
    this.below.addChild(await this.buildWood(min, w, h));
    this.below.addChild(this.buildTrackMarkings());
    this.below.addChild(...(await this.buildSurfaceRegions()));
    this.below.addChild(this.buildEdgeShadow(min, max));

    const propsUnder = new Container();
    const propsOver = new Container();
    for (const placement of this.track.props) {
      const sprite = await this.buildPropSprite(placement);
      if (!sprite) continue;
      (placement.layer === 'under' ? propsUnder : propsOver).addChild(sprite);
    }
    this.below.addChild(propsUnder);
    this.above.addChild(propsOver);

    this.below.addChild(this.buildSkidLayer(w, h));
  }

  // Paints one tire-mark daub into the persistent skid render texture, in
  // world coordinates. Called by RaceScene while a car is drifting on wood.
  paintSkid(pos: Vec2, heading: number, alpha: number): void {
    const tx = (pos.x - this.skidOriginX) * SKID_PX_PER_M;
    const ty = (pos.y - this.skidOriginY) * SKID_PX_PER_M;

    this.skidBrush.clear();
    this.skidBrush.ellipse(0, 0, SKID_MARK_LENGTH_PX, SKID_MARK_WIDTH_PX).fill({ color: SKID_COLOR, alpha });
    this.skidBrush.rotation = heading;
    this.skidBrush.position.set(tx, ty);

    if (!this.skidTexture) return;
    this.app.renderer.render({ container: this.skidBrush, target: this.skidTexture, clear: false });
  }

  private buildFloor(min: Vec2, max: Vec2): Graphics {
    return new Graphics()
      .rect(min.x - FLOOR_MARGIN_M, min.y - FLOOR_MARGIN_M, max.x - min.x + FLOOR_MARGIN_M * 2, max.y - min.y + FLOOR_MARGIN_M * 2)
      .fill(FLOOR_COLOR);
  }

  private async buildWood(min: Vec2, w: number, h: number): Promise<TilingSprite> {
    const texture = await Assets.load<Texture>(SURFACE_PATHS.table_wood);
    const tile = new TilingSprite({ texture, width: w, height: h });
    tile.position.set(min.x, min.y);
    tile.tileScale.set(WOOD_TILE_SIZE_M / texture.width, WOOD_TILE_SIZE_M / texture.height);
    return tile;
  }

  // Builds the worn path band, its dashed edge lines and the checkered
  // start/finish strip into one Graphics node. Called once in build(), in
  // world metres, between the wood surface and the surface regions so props
  // (crumbs, milk spill, the tablecloth) still layer on top of it.
  private buildTrackMarkings(): Graphics {
    const g = new Graphics();
    const half = this.track.width / 2;
    this.buildDashedEdge(g, half);
    this.buildDashedEdge(g, -half);
    this.buildStartStrip(g, half);
    return g;
  }

  // Dashed cream line tracing one edge of the band (lateral = +/- half
  // width). Built in two passes:
  //
  // 1. Sample the offset curve (pointAtProgress at EDGE_STEP_M, pushed out
  //    by `lateral` along the tangent's right vector) at fixed arc-length
  //    steps, keeping each sample's source progress and tangent.
  // 2. Prune inner-corner loops: on the inside of a tight bend (e.g. the
  //    hairpin), the raw offset curve can fold back on itself and trace a
  //    small loop, which used to render as a dashed line doubling back on
  //    itself. This is the classic offset-curve loop-removal fix: walk
  //    the samples and drop any point whose incoming segment (from the
  //    last surviving point) points opposite to the centreline's own
  //    direction of travel there (negative dot product with the local
  //    tangent) - such a point is only reachable by backtracking, so it's
  //    part of the loop, not the edge.
  //
  // The remaining points are then dashed using their ORIGINAL centreline
  // progress for the dash/gap pattern (not the pruned polyline's own arc
  // length), so dash spacing stays tied to true distance along the racing
  // line even where a stretch of the offset curve was dropped.
  private buildDashedEdge(g: Graphics, lateral: number): void {
    const length = this.track.length;
    const steps = Math.max(4, Math.round(length / EDGE_STEP_M));

    const raw: { p: Vec2; s: number; tangent: number }[] = [];
    for (let i = 0; i <= steps; i++) {
      const s = (i / steps) * length;
      const { pos, tangent } = pointAtProgress(this.track, s);
      const dir = v.fromAngle(tangent);
      const right = { x: -dir.y, y: dir.x };
      raw.push({ p: v.add(pos, v.scale(right, lateral)), s, tangent });
    }

    const pruned: { p: Vec2; s: number }[] = [];
    let prevPoint: Vec2 | null = null;
    for (const sample of raw) {
      if (prevPoint) {
        const segment = v.sub(sample.p, prevPoint);
        const tangentDir = v.fromAngle(sample.tangent);
        if (v.dot(segment, tangentDir) < 0) continue; // inner-corner loop point, drop it
      }
      pruned.push({ p: sample.p, s: sample.s });
      prevPoint = sample.p;
    }

    const pattern = EDGE_DASH_M + EDGE_GAP_M;
    let drawing = false;
    for (const sample of pruned) {
      const patternPos = ((sample.s % pattern) + pattern) % pattern;
      const on = patternPos < EDGE_DASH_M;
      if (on) {
        if (!drawing) {
          g.moveTo(sample.p.x, sample.p.y);
          drawing = true;
        } else {
          g.lineTo(sample.p.x, sample.p.y);
        }
      } else {
        drawing = false;
      }
    }
    g.stroke({ width: EDGE_LINE_WIDTH_M, color: EDGE_LINE_COLOR, alpha: EDGE_LINE_ALPHA });
  }

  // Two rows of alternating cream/dark squares spanning the band's full
  // width, centred on progress 0 and perpendicular to the tangent there -
  // a small enough span (START_STRIP_DEPTH_M) that treating the ground as
  // flat across it (ignoring track curvature) reads fine.
  private buildStartStrip(g: Graphics, half: number): void {
    const { pos, tangent } = pointAtProgress(this.track, 0);
    const dir = v.fromAngle(tangent);
    const right = { x: -dir.y, y: dir.x };
    const rowDepth = START_STRIP_DEPTH_M / 2;
    const cols = Math.max(2, Math.ceil(this.track.width / START_STRIP_SQUARE_M));
    const colWidth = this.track.width / cols;
    const corner = (along: number, lateral: number): Vec2 => v.add(pos, v.add(v.scale(dir, along), v.scale(right, lateral)));

    for (let row = 0; row < 2; row++) {
      const alongNear = -START_STRIP_DEPTH_M / 2 + row * rowDepth;
      const alongFar = alongNear + rowDepth;
      for (let col = 0; col < cols; col++) {
        const latNear = -half + col * colWidth;
        const latFar = latNear + colWidth;
        const a = corner(alongNear, latNear);
        const b = corner(alongNear, latFar);
        const c = corner(alongFar, latFar);
        const d = corner(alongFar, latNear);
        const dark = (row + col) % 2 === 0;
        g.poly([a.x, a.y, b.x, b.y, c.x, c.y, d.x, d.y]).fill(dark ? START_STRIP_DARK : START_STRIP_CREAM);
      }
    }
  }

  private async buildSurfaceRegions(): Promise<Container[]> {
    const nodes: Container[] = [];
    for (const region of this.track.surfaces) {
      const box = bboxOf(region.poly);
      const width = box.max.x - box.min.x;
      const height = box.max.y - box.min.y;
      const cx = (box.min.x + box.max.x) / 2;
      const cy = (box.min.y + box.max.y) / 2;

      if (region.name === 'tablecloth') {
        const texture = await Assets.load<Texture>(SURFACE_PATHS.tablecloth);
        const tile = new TilingSprite({ texture, width, height });
        tile.position.set(box.min.x, box.min.y);
        tile.tileScale.set(TABLECLOTH_TILE_SIZE_M / texture.width, TABLECLOTH_TILE_SIZE_M / texture.height);
        nodes.push(tile);
        continue;
      }

      const propKey = region.name as PropKey;
      if (!(propKey in PROP_PATHS)) continue;
      const texture = await Assets.load<Texture>(PROP_PATHS[propKey]);
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.width = width;
      sprite.height = height;
      sprite.position.set(cx, cy);
      nodes.push(sprite);
    }
    return nodes;
  }

  // A stack of shrinking, fading "picture frame" bands hugging the outside
  // of the table rect: reads as a soft shadow falling off the table edge
  // into the darker floor, without needing a true gradient fill.
  private buildEdgeShadow(min: Vec2, max: Vec2): Graphics {
    const g = new Graphics();
    for (let i = 0; i < EDGE_BAND_COUNT; i++) {
      const inner = i * EDGE_BAND_WIDTH_M;
      const outer = inner + EDGE_BAND_WIDTH_M;
      const alpha = EDGE_BAND_MAX_ALPHA * (1 - i / EDGE_BAND_COUNT);
      drawFrameBand(g, min, max, inner, outer, EDGE_COLOR, alpha);
    }
    return g;
  }

  private async buildPropSprite(placement: PropPlacement): Promise<Sprite | null> {
    const key = placement.sprite as PropKey;
    if (!(key in PROP_PATHS)) return null;
    const texture = await Assets.load<Texture>(PROP_PATHS[key]);
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    sprite.width = propMetres(texture.width, placement.scale);
    sprite.height = propMetres(texture.height, placement.scale);
    sprite.position.set(placement.pos.x, placement.pos.y);
    sprite.rotation = placement.rot;
    return sprite;
  }

  // Releases the GPU resources this renderer owns: the skid RenderTexture
  // (allocated at SKID_PX_PER_M over the whole track, so far and away the
  // largest single allocation in a race) and the brush Graphics, plus the
  // two layer containers and everything built into them. Textures loaded
  // through Assets are deliberately NOT destroyed: they are shared and
  // cached, and the next race reuses them.
  destroy(): void {
    this.skidBrush.destroy();
    // The skid layer Sprite lives under `below` and is destroyed with it;
    // the RenderTexture behind it is not, so it goes explicitly.
    this.below.destroy({ children: true });
    this.above.destroy({ children: true });
    this.skidTexture?.destroy(true);
  }

  private buildSkidLayer(w: number, h: number): Sprite {
    this.skidTexture = RenderTexture.create({ width: Math.ceil(w * SKID_PX_PER_M), height: Math.ceil(h * SKID_PX_PER_M) });
    const sprite = new Sprite(this.skidTexture);
    sprite.position.set(this.skidOriginX, this.skidOriginY);
    sprite.width = w;
    sprite.height = h;
    return sprite;
  }
}

function drawFrameBand(g: Graphics, min: Vec2, max: Vec2, inner: number, outer: number, color: number, alpha: number): void {
  const w = max.x - min.x;
  const h = max.y - min.y;
  // Top strip
  g.rect(min.x - outer, min.y - outer, w + outer * 2, outer - inner).fill({ color, alpha });
  // Bottom strip
  g.rect(min.x - outer, max.y + inner, w + outer * 2, outer - inner).fill({ color, alpha });
  // Left strip (between the top/bottom strips)
  g.rect(min.x - outer, min.y - inner, outer - inner, h + inner * 2).fill({ color, alpha });
  // Right strip
  g.rect(max.x + inner, min.y - inner, outer - inner, h + inner * 2).fill({ color, alpha });
}
